import { Box, Stack, useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CardsColumn,
  CardSlot,
  CardSlotInner,
  DotButton,
  DotColumn,
  NavigationWrapper
} from "./ActivityDotNavigation.styles";
import type { ActivityDotNavigationProps } from "./ActivityDotNavigation.types";
import { CourseActivityCard } from "./CourseActivityCard.component";
import { useActivityAndCourseSelection } from "../../components/DetailsEsploraRestyle/IndexActivitySelected.context";

export function ActivityDotNavigation({
  activities,
  initialActiveLoId,
  courseId,
  deepLinkLanding = false
}: ActivityDotNavigationProps) {
  const theme = useTheme();

  const [advancedNavigation, setAdvancedNavigation] = useState<boolean>(false);

  const [indexFirstDot, setIndexFirstDot] = useState(0);
  const [blinkId, setBlinkId] = useState<number | null>(null);
  const landingHandled = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const { activityIdSelected, setActivityIdSelected, syncUrlToActivity } = useActivityAndCourseSelection();

  const maxActivitiesVisible = 8;

  const maxStart = activities.length - maxActivitiesVisible;
  const indexesDotList = [
    indexFirstDot - 1,
    ...Array.from({ length: maxActivitiesVisible }, (_, k) => indexFirstDot + k),
    indexFirstDot + maxActivitiesVisible
  ];

  const dots = () => {
    if(!advancedNavigation) {
      return activities.map((activity, i) => {
        return {
          ...activity,
          index: i,
          small: false,
          active: activityIdSelected === activity?.id,
          onClick: () => {
            handleDotClick(i);
          }
        };
      });
    }
    return indexesDotList
      .filter(i => i >= 0 && i <= activities.length - 1)
      .map(i => {
        const isShrunk = i === indexFirstDot - 1 || i === indexFirstDot + maxActivitiesVisible;

        return {
          index: i,
          small: isShrunk,
          active: activityIdSelected === activities[i]?.id,
          onClick: () => {
            handleDotClick(i);
            if (i === indexFirstDot + maxActivitiesVisible) {
              setIndexFirstDot(s => Math.min(s + 1, maxStart));
              return;
            }
            if (i === indexFirstDot - 1) {
              setIndexFirstDot(s => Math.max(s - 1, 0));
              return;
            }
          }
        };
      });
  };

  const handleDotClick = useCallback((index: number) => {
    setActivityIdSelected(activities[index].id);
    syncUrlToActivity(activities[index]);
  }, [activities]);

  useEffect(() => {
    if (!initialActiveLoId) return;
    setActivityIdSelected(initialActiveLoId);

    const targetIndex = activities.findIndex(a => a.id === initialActiveLoId);
    if (targetIndex < 0) return;

    const clampedMaxStart = Math.max(0, activities.length - maxActivitiesVisible);

    const start = Math.min(
      Math.max(targetIndex - Math.floor(maxActivitiesVisible / 2), 0),
      clampedMaxStart
    );
    setIndexFirstDot(start);
  }, [initialActiveLoId, activities, setActivityIdSelected]);

  
  useEffect(() => {
    if (!deepLinkLanding || landingHandled.current || !initialActiveLoId) return;
    const targetIndex = activities.findIndex(a => a.id === initialActiveLoId);
    if (targetIndex < 0) return;
    landingHandled.current = true;

    const targetTypology = activities[targetIndex]?.learningObjectTypology;
    const isClassroomTarget = targetTypology === "DA_VIRTUAL_CLASS" || targetTypology === "DA_PHYSICAL_CLASS";

    setTimeout(() => {
      if (!isMounted.current) return;
      document.getElementById(`card-content-${initialActiveLoId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (isClassroomTarget) setBlinkId(initialActiveLoId);
    }, 350);
    setTimeout(() => {
      if (isMounted.current) setBlinkId(null);
    }, 2200);
  }, [deepLinkLanding, initialActiveLoId, activities]);

  useEffect(() => {
    setAdvancedNavigation && setAdvancedNavigation(activities?.length > 8);
  }, []);

  const isCardActive = (index: number) => activityIdSelected === activities[index].id;
  // if card index is above the active index, the card must be shown
  //const isCardVisible = (index: number) => index >= activeIndex; // nascondo le card precedenti a quella attiva
  const isCardVisible = (index: number) => {
    if (!advancedNavigation) return true;
    if (isCardActive(index)) return true;
    return index >= indexFirstDot && index < indexFirstDot + maxActivitiesVisible;
  };

  return (
    <NavigationWrapper>
      <Box
        position={ "absolute" }
        top={ advancedNavigation ? "unset" : "0" }
      >
        <DotColumn
          tabIndex={ -1 }
          role="tablist"
        >
          { dots()?.map((dot) => (
            <Stack
              key={ dot.index }
              tabIndex={ -1 }
              aria-selected={ isCardActive(dot.index) }
              role="tab"
              onClick={ () => dot.onClick() }
              sx={ {
                alignItems: "center",
                cursor: "pointer",
                height: "1.25rem",
                justifyContent: "center",
                width: "1.5rem",
                ...!isCardActive(dot.index) && {
                  "&:hover > span": {
                    background: theme.customColors.textPrimary
                  }
                }
              } }
            >
              <DotButton
                tabIndex={ -1 }
                $active={ isCardActive(dot.index) }
                $isComplete={ activities[dot.index].status === "C" }
                $isNavigationDot={ dot.small ?? null }
              />
            </Stack>
          )) }
        </DotColumn>

      </Box>

      <CardsColumn
        id={ `cards-column-${courseId}` }
        marginLeft={ "2.5rem" }
      >
        { activities?.map((activity, index) => (
          <CardSlot
            key={ index }
            className="cardSlot"

            sx={ {
              borderRadius: "0.5rem",
              ...isCardVisible(index) && {
                gridTemplateRows: "1fr",
                opacity: 1,
                pointerEvents: "auto"
              }
            } }
          >
            <CardSlotInner
              className="cardSlotInner"
            >
              <CourseActivityCard
                { ...activity }
                activity={ activity }
                index={ index + 1 }
                realIndex={ index }
                isActive={ isCardActive(index) }
                isBlinking={ blinkId === activity.id }
                handleActivityClick={ (i) => handleDotClick(i as number) }
                madeAttempts={ undefined }
              />
            </CardSlotInner>
          </CardSlot>
        )) }
      </CardsColumn>
    </NavigationWrapper>
  );
}
